import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../../styles/colors';
import { typography } from '../../styles/typography';
import { spacing } from '../../styles/spacing';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

// Modal overlay
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  z-index: ${spacing.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[4]};
  animation: ${fadeIn} 0.2s ease-out;
`;

// Modal container
const ModalContainer = styled(motion.div)<{ $size: 'sm' | 'md' | 'lg' | 'xl' | 'full' }>`
  background: ${colors.background.primary};
  border-radius: ${spacing.radius['2xl']};
  box-shadow: ${colors.shadows['2xl']};
  border: 1px solid ${colors.border.primary};
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
  position: relative;
  
  ${props => {
    switch (props.$size) {
      case 'sm': return css`max-width: 400px; width: 100%;`;
      case 'md': return css`max-width: 500px; width: 100%;`;
      case 'lg': return css`max-width: 700px; width: 100%;`;
      case 'xl': return css`max-width: 900px; width: 100%;`;
      case 'full': return css`max-width: 95vw; width: 100%;`;
      default: return css`max-width: 500px; width: 100%;`;
    }
  }}
`;

// Modal header
const ModalHeader = styled.div`
  padding: ${spacing[4]} ${spacing[6]};
  border-bottom: 1px solid ${colors.border.primary};
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

// Modal title
const ModalTitle = styled.h2`
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.lg};
  font-weight: ${typography.weights.medium};
  color: ${colors.text.primary};
  margin: 0;
  line-height: ${typography.lineHeights.snug};
`;

// Modal description
const ModalDescription = styled.p`
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.base};
  color: ${colors.text.secondary};
  margin: ${spacing[2]} 0 0 0;
  line-height: ${typography.lineHeights.relaxed};
`;

// Modal content
const ModalContent = styled.div`
  padding: ${spacing[6]};
  flex: 1;
  overflow-y: auto;
`;

// Modal footer
const ModalFooter = styled.div`
  padding: ${spacing[4]} ${spacing[6]} ${spacing[6]};
  border-top: 1px solid ${colors.border.primary};
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
  flex-shrink: 0;
`;

// Close button
const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: ${spacing[4]};
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: ${spacing.radius.full};
  border: none;
  background: ${colors.background.secondary};
  color: ${colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  z-index: 1;
  
  &:hover {
    background: ${colors.background.tertiary};
    color: ${colors.text.primary};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

// Modal props interface
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

// Main Modal component
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  showCloseButton = true,
  children,
  footer,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContainer
            $size={size}
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {(title || description || showCloseButton) && (
              <ModalHeader>
                {title && <ModalTitle>{title}</ModalTitle>}
                {description && <ModalDescription>{description}</ModalDescription>}
                {showCloseButton && (
                  <CloseButton onClick={onClose} aria-label="Close modal">
                    ✕
                  </CloseButton>
                )}
              </ModalHeader>
            )}
            
            <ModalContent>
              {children}
            </ModalContent>
            
            {footer && <ModalFooter>{footer}</ModalFooter>}
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default Modal;
